import {
  Autocomplete,
  Box,
  Collapse,
  Divider,
  Grid,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CitiesAndCountiesAutocomplete from "../../../../common/components/functional/fields/citiesAndCountiesAutocomplete/CitiesAndCountiesAutocomplete";
import PhoneField from "../../../../common/components/functional/phoneField/PhoneField";
import getLocale from "../../../../common/helpers/getLocale";
import {
  PHONE_NUMBER_REGEX,
  VALID_EMAIL_REGEX,
} from "../../../../common/helpers/regexes";
import { CityAndCounty } from "../../../../common/types/apiTypes";
import { WorkTitle } from "../../../orders/orderAPI";
import { selectAllExpertUsers } from "../../../user/expertUsersSlice";
import { fetchWorkTitles } from "../../../workTitles/workTitleSlice";
import { StyledSelect } from "../../styles";
import {
  ContractType,
  EmploymentType,
  JobAd,
  JobCategory,
  WorkTimeType,
} from "../../types/common";
import { changeFormValues } from "../../utils";
import FormGrid from "../FormGrid";
import FormSectionHeader from "../FormSectionHeader";
import PreviewField from "../PreviewField";
import {
  getEndDateValidationError,
  getStartDateValidationError,
} from "../dateValidation";
import "../quill.css";

// Something wrong with typescript handling the import, using a workaround: https://github.com/zenoamaro/react-quill/issues/230
const Quill = ReactQuill as any;

type Props = {
  selectedJobAd?: JobAd;
  isEditing: boolean;
};

type Expert = {
  id: string;
  name: string;
  email: string;
};

type WorkTitleWithoutName = Omit<WorkTitle, "name">;

const GeneralSection = ({ selectedJobAd, isEditing }: Props) => {
  const user = useAppSelector((state) => state.user.user);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(
    user
      ? {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        }
      : null,
  );
  const [workTitles, setWorkTitles] = useState<WorkTitleWithoutName[]>([]);
  const dispatch = useAppDispatch();
  const { fieldOfWork: localizedFieldsOfWork, workTitle: localizedWorkTitle } =
    useAppSelector((state) => state.localized);
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getFieldState,
    trigger,
  } = useFormContext();

  const handleToggle = () => {
    setIsOpen((prevOpen) => !prevOpen);
  };
  const selectedFieldOfWork = watch("fieldOfWorkId");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const quillModules = useMemo(() => {
    return {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "link"],
        [{ list: "ordered" }, { list: "bullet" }],
      ],
      clipboard: {
        matchVisual: false,
      },
    };
  }, []);

  const allExperts = useAppSelector((state) => selectAllExpertUsers(state));

  useEffect(() => {
    const experts = allExperts
      .filter(
        (expert) =>
          expert.account.registered &&
          (expert.account.enabled == null || expert.account.enabled === true),
      )
      .map((expert) => ({
        id: expert.id,
        name: `${expert.account.firstName} ${expert.account.lastName}`,
        email: expert.account.emailAddress,
      }));
    setExperts(experts);

    dispatch(fetchWorkTitles({ language: getLocale().code || "" })).then(
      (action) => {
        if (isFulfilled(action)) {
          // NOTE: We drop name and use localizedWorkTitle to translate work titles based on id
          const workTitlesWithoutName = action.payload.map((wt) => ({
            id: wt.id,
            fieldOfWorkId: wt.fieldOfWorkId,
          }));
          setWorkTitles(workTitlesWithoutName);
        }
      },
    );
  }, [dispatch, allExperts]);

  // Validate form when opening existing jobAd
  useEffect(() => {
    if (selectedJobAd && isEditing) {
      trigger();
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobAd, isEditing]);

  useEffect(() => {
    if ((startDate && endDate) || (isEditing && endDate)) {
      trigger("endDate"); // Re-validate endDate
    }
  }, [startDate, isEditing, endDate, trigger]);

  const sortedContactPersons = useMemo(
    () => experts.sort((a, b) => a.name.localeCompare(b.name)),
    [experts],
  );

  const sortedFieldOfWorkIds = useMemo(
    () =>
      Object.entries(localizedFieldsOfWork)
        .sort(([, valueA], [, valueB]) => valueA.localeCompare(valueB))
        .map(([key]) => key),
    [localizedFieldsOfWork],
  );

  const sortedFilteredWorkTitles = useMemo(
    () =>
      workTitles
        .filter(
          (o: WorkTitleWithoutName) => o.fieldOfWorkId === selectedFieldOfWork,
        )
        .sort((a, b) => {
          const translatedA = localizedWorkTitle[a.id];
          const translatedB = localizedWorkTitle[b.id];
          return translatedA.localeCompare(translatedB);
        }),
    [workTitles, selectedFieldOfWork, localizedWorkTitle],
  );

  return (
    <>
      <FormSectionHeader
        isOpen={isOpen}
        toggleOpen={handleToggle}
        title={t("jobAds:general")}
        forcePublish
      />
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Grid
          container
          alignItems="center"
          columnSpacing={2}
          rowGap={1}
          maxWidth={"1000px"}
        >
          <FormGrid title={t("jobAds:state")} contentSize={3}>
            <Box height={40} display="flex" alignItems="center">
              <Typography>
                {selectedJobAd
                  ? t(`jobAds:${selectedJobAd.status}`)
                  : t("jobAds:DRAFT")}
              </Typography>
            </Box>
          </FormGrid>
          <FormGrid title={t("jobAds:adTitle")} contentSize={4} required>
            <Controller
              name="title"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  // Max 100 characters
                  if (value.length > 100) {
                    return t("jobAds:validation:titleTooLong");
                  }
                },
              }}
              render={({ field }) => (
                <>
                  {isEditing ? (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      error={!!errors.title}
                      helperText={
                        errors.title?.message &&
                        typeof errors.title.message === "string"
                          ? errors.title.message
                          : ""
                      }
                      FormHelperTextProps={{
                        style: {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  ) : (
                    <PreviewField text={field.value} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:startDate")} contentSize={2}>
            <Controller
              name="startDate"
              control={control}
              rules={{
                validate: (value) => {
                  const validationError = getStartDateValidationError(value);
                  return validationError === null || t(validationError);
                },
              }}
              render={({ field }) => (
                <>
                  {isEditing ? (
                    <DesktopDatePicker
                      {...field}
                      onChange={(value: Date | null) => {
                        field.onChange(value);
                      }}
                      value={field.value}
                      format="dd.MM.yyyy"
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!errors.startDate,
                          helperText:
                            typeof errors.startDate?.message === "string"
                              ? errors.startDate.message
                              : "",
                          FormHelperTextProps: {
                            style: {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          },
                        },
                      }}
                      disablePast
                      maxDate={endDate}
                    />
                  ) : (
                    <PreviewField
                      text={field?.value?.toLocaleDateString("fi-FI")}
                    />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:endDate")} contentSize={2} required>
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (!value) return true;
                  const validationError = getEndDateValidationError(
                    value,
                    startDate,
                  );

                  return validationError === null || t(validationError);
                },
              }}
              render={({ field }) => (
                <>
                  {isEditing ? (
                    <DesktopDatePicker
                      {...field}
                      onChange={(value: Date | null) => {
                        field.onChange(value);
                      }}
                      format="dd.MM.yyyy"
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!errors.endDate,
                          helperText:
                            typeof errors.endDate?.message === "string"
                              ? errors.endDate.message
                              : "",
                          FormHelperTextProps: {
                            style: {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          },
                        },
                      }}
                      disablePast
                      minDate={startDate}
                    />
                  ) : (
                    <PreviewField
                      text={field?.value?.toLocaleDateString("fi-FI")}
                    />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid
            title={t("jobAds:location")}
            contentSize={4}
            required
            infoText={t("jobAds:locationTooltip")}
          >
            <Controller
              name="locations"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (!value || value.length === 0) {
                    return t("jobAds:validation:minLocations");
                  }

                  if (value.length > 5) {
                    return t("jobAds:validation:maxLocations");
                  }
                },
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  {isEditing ? (
                    <CitiesAndCountiesAutocomplete
                      multiple
                      required
                      value={value || []}
                      onChange={onChange}
                      error={!!errors.locations}
                    />
                  ) : (
                    <PreviewField
                      text={value.map((i: CityAndCounty) => i.city).join(", ")}
                    />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid
            title={t("jobAds:jobAdTextFinnish")}
            contentSize={8}
            required
          >
            <Controller
              name="jobAdTextFinnish"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  // Allow only specific HTML tags
                  const allowedTagsRegex =
                    /<(?!\/?(p|a|u|em|strong|cite|blockquote|code|h1|h2|h3|ul|ol|li|dl|dt|dd|br)\b)[^>]+>/i;

                  if (allowedTagsRegex.test(value)) {
                    return t("jobAds:validation:unwantedHTML");
                  }

                  // Max 8000 characters
                  if (value?.length > 8000)
                    return t("jobAds:validation:maxLength");

                  return true; // Pass validation if no errors
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <Quill
                    value={field.value}
                    modules={quillModules}
                    onChange={field.onChange}
                    className={
                      !isEditing
                        ? "preview"
                        : fieldState.error
                          ? "ql-error"
                          : null
                    }
                    readOnly={!isEditing}
                  />
                  {fieldState.error && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "0.75rem",
                        marginTop: "4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {fieldState.error.message}
                    </div>
                  )}
                </>
              )}
            />
          </FormGrid>
          {/* Hide English jobAdText for now (see YLPE-2405)
          <FormGrid
            title={t("jobAds:jobAdTextEnglish")}
            contentSize={8}
            required
          >
            <Controller
              name="jobAdTextEnglish"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <Quill
                  value={field.value}
                  modules={quillModules}
                  onChange={field.onChange}
                  className={
                    !isEditing
                      ? "preview"
                      : !!errors.jobAdTextEnglish
                        ? "ql-error"
                        : null
                  }
                  readOnly={!isEditing}
                />
              )}
            />
          </FormGrid>
          */}
          <FormGrid
            title={t("jobAds:contactPersonName")}
            contentSize={4}
            required
          >
            <Controller
              name="contactPersonName"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  {isEditing ? (
                    <Autocomplete
                      sx={{
                        backgroundColor: "rgba(249, 249, 250, 0.6)",
                        maxWidth: "372px",
                      }}
                      value={value || selectedExpert || null}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.name
                      }
                      options={sortedContactPersons}
                      isOptionEqualToValue={(option, value) => {
                        const optionId =
                          typeof option === "string" ? option : option?.name;
                        const valueId =
                          typeof value === "string" ? value : value.name;
                        return optionId === valueId;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          name="contactPersonName"
                          required
                          error={!!errors.contactPersonName}
                        />
                      )}
                      onChange={(_, newValue: Expert | string | null) => {
                        if (!newValue || typeof newValue === "string") {
                          onChange(null);
                          setSelectedExpert(null);
                          // Clear related fields
                          setValue("contactPersonMail", null, {
                            shouldValidate: true,
                          });
                          setValue("contactPersonId", null, {
                            shouldValidate: true,
                          });
                        } else {
                          setSelectedExpert(newValue);
                          onChange(newValue.name);
                          setValue("contactPersonMail", newValue.email, {
                            shouldValidate: true,
                          });
                          setValue("contactPersonId", newValue.id, {
                            shouldValidate: true,
                          });
                        }
                      }}
                    />
                  ) : (
                    <PreviewField text={value} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid
            title={t("jobAds:contactPersonMail")}
            contentSize={4}
            required
          >
            <Controller
              name="contactPersonMail"
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: VALID_EMAIL_REGEX,
                  message: t("customers:usersNS:invalidEmail"),
                },
              }}
              render={({ field }) => (
                <>
                  {isEditing ? (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      error={!!errors.contactPersonMail}
                      value={field.value || ""}
                    />
                  ) : (
                    <PreviewField text={field.value} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:contactPersonPhone")} contentSize={4}>
            <Controller
              name="contactPersonPhone"
              control={control}
              rules={{
                pattern: { value: PHONE_NUMBER_REGEX, message: "" },
              }}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <PhoneField
                      value={value}
                      onChange={onChange}
                      fullWidth={false}
                      error={!!errors.contactPersonPhone}
                    />
                  ) : (
                    <PreviewField text={value} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("fieldOfWork")} contentSize={4} required>
            <Controller
              name="fieldOfWorkId"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <Autocomplete
                      fullWidth
                      value={value || null}
                      options={sortedFieldOfWorkIds}
                      renderOption={(props, optionId) => (
                        <ListItem {...props} key={optionId}>
                          <ListItemText>
                            {localizedFieldsOfWork[optionId]}
                          </ListItemText>
                        </ListItem>
                      )}
                      getOptionLabel={(optionId) =>
                        localizedFieldsOfWork[optionId]
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          name="fieldOfWorkTitle"
                          error={!!errors.fieldOfWorkId}
                        />
                      )}
                      onChange={(_, newValue) => {
                        onChange(newValue);
                        setValue("workTitle", null);
                      }}
                    />
                  ) : (
                    <PreviewField text={localizedFieldsOfWork[value]} />
                  )}
                </>
              )}
            />
          </FormGrid>
          {
            //TODO: editing worktitle breaks it
          }
          <FormGrid title={t("jobAds:workTitle")} contentSize={4} required>
            <Controller
              name="workTitle"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <Autocomplete
                      fullWidth
                      value={value || null}
                      options={sortedFilteredWorkTitles}
                      renderOption={(props, option) => (
                        <ListItem {...props} key={option.id}>
                          <ListItemText>
                            {localizedWorkTitle[option.id]}
                          </ListItemText>
                        </ListItem>
                      )}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") {
                          return localizedWorkTitle[option];
                        }
                        return localizedWorkTitle[option.id];
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (typeof value === "string") {
                          return option.id === value;
                        }
                        return option.id === value.id;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          name="workTitle"
                          error={!!errors.workTitle}
                        />
                      )}
                      onChange={(_, newValue) => {
                        onChange(newValue);
                      }}
                    />
                  ) : (
                    <PreviewField text={localizedWorkTitle[value]} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:workTime")} contentSize={4} required>
            <Controller
              name="workTimeType"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <StyledSelect
                      value={value}
                      onChange={(event) => {
                        onChange(event.target.value);

                        if (!selectedJobAd) {
                          if (event.target.value === WorkTimeType.FULL_TIME) {
                            changeFormValues({
                              fields: {
                                jobCategoryJobly: JobCategory.FULL_TIME,
                              },
                              setValue,
                              getFieldState,
                            });
                          } else if (
                            event.target.value === WorkTimeType.PART_TIME
                          ) {
                            changeFormValues({
                              fields: {
                                jobCategoryJobly: JobCategory.PART_TIME,
                              },
                              setValue,
                              getFieldState,
                            });
                          }
                        }
                      }}
                      size="small"
                      error={!!errors.workTimeType}
                      MenuProps={{
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value={"PART_TIME"}>
                        {t("jobAds:PART_TIME")}
                      </MenuItem>
                      <MenuItem value={"FULL_TIME"}>
                        {t("jobAds:FULL_TIME")}
                      </MenuItem>
                    </StyledSelect>
                  ) : (
                    <PreviewField text={t(`jobAds:${value}`)} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:employmentType")} contentSize={4} required>
            <Controller
              name="employmentType"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <StyledSelect
                      value={value}
                      onChange={(event) => {
                        onChange(event.target.value);

                        if (
                          event.target.value === EmploymentType.INDEFINITE
                        ) {
                          setValue("durationOfTemporaryEmployment", "", {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }

                        if (!selectedJobAd) {
                          if (event.target.value === EmploymentType.TEMPORARY) {
                            changeFormValues({
                              fields: {
                                contractTypeJobly:
                                  ContractType.FIXED_TERM_EMPLOYMENT,
                              },
                              setValue,
                              getFieldState,
                            });
                          } else if (
                            event.target.value === EmploymentType.SUMMER_WORK
                          ) {
                            changeFormValues({
                              fields: {
                                contractTypeJobly: ContractType.SUMMER_JOB,
                              },
                              setValue,
                              getFieldState,
                            });
                          } else {
                            changeFormValues({
                              fields: {
                                contractTypeJobly: "",
                              },
                              setValue,
                              getFieldState,
                            });
                          }
                        }
                      }}
                      size="small"
                      error={!!errors.employmentType}
                      MenuProps={{
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value={"TEMPORARY"}>
                        {t("jobAds:TEMPORARY")}
                      </MenuItem>
                      <MenuItem value={"INDEFINITE"}>
                        {t("jobAds:INDEFINITE")}
                      </MenuItem>
                      <MenuItem value={"SUMMER_WORK"}>
                        {t("jobAds:SUMMER_WORK")}
                      </MenuItem>
                      <MenuItem value={"SEASONAL_WORK"}>
                        {t("jobAds:SEASONAL_WORK")}
                      </MenuItem>
                    </StyledSelect>
                  ) : (
                    <PreviewField text={t(`jobAds:${value}`)} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:salary")} contentSize={4}>
            <Controller
              name="salary"
              control={control}
              rules={{
                maxLength: 100,
              }}
              render={({ field }) => (
                <>
                  {isEditing ? (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      error={!!errors.salary}
                    />
                  ) : (
                    <PreviewField text={field.value} />
                  )}
                </>
              )}
            />
          </FormGrid>
          <FormGrid title={t("jobAds:remoteWork")} contentSize={2}>
            <Controller
              name="remoteWork"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {isEditing ? (
                    <StyledSelect
                      value={value === null ? "" : value.toString()}
                      onChange={(event) => {
                        const val = event.target.value;
                        if (val === "true") {
                          onChange(true);
                        } else if (val === "false") {
                          onChange(false);
                        } else {
                          onChange(null);
                        }
                      }}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">-</MenuItem>
                      <MenuItem value="true">{t("yes")}</MenuItem>
                      <MenuItem value="false">{t("no")}</MenuItem>
                    </StyledSelect>
                  ) : (
                    <PreviewField
                      text={value === null ? "-" : value ? t("yes") : t("no")}
                    />
                  )}
                </>
              )}
            />
          </FormGrid>
        </Grid>
      </Collapse>
      <Divider
        sx={{
          margin: "24px 0 16px 0",
        }}
      />
    </>
  );
};

export default GeneralSection;
